// src/pages/AdminPage/modals/EditNotificationModal.tsx
import { useState, useEffect, useRef } from "react";
import { X, Upload, ImageOff } from "lucide-react";
import type { NotificationItem } from "../utils/adminTypes";

const IMG_BASE = process.env.REACT_APP_BASE_URL_IMAGE;

interface NotificationForm {
  title: string;
  text: string;
  image?: File;
}

interface EditNotificationModalProps {
  notification: NotificationItem | null; // null = yangi yaratish
  open: boolean;
  onClose: () => void;
  onSave: (id: number | null, data: NotificationForm) => Promise<void>;
}

const emptyForm: NotificationForm = { title: "", text: "" };

export default function EditNotificationModal({
  notification,
  open,
  onClose,
  onSave,
}: EditNotificationModalProps) {
  const [form, setForm] = useState<NotificationForm>(emptyForm);
  const [preview, setPreview] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (notification) {
      setForm({ title: notification.title, text: notification.text });
      setPreview(
        notification.image_url ? `${IMG_BASE}/${notification.image_url}` : null,
      );
    } else {
      setForm(emptyForm);
      setPreview(null);
    }
  }, [notification, open]);

  if (!open) return null;

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) return;
    if (file.size > 5 * 1024 * 1024) return;
    setForm({ ...form, image: file });
    setPreview(URL.createObjectURL(file));
  };

  const handleSave = async () => {
    if (!form.title.trim() || !form.text.trim()) return;
    setSaving(true);
    try {
      await onSave(notification?.id || null, form);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  const inputStyle: React.CSSProperties = {
    width: "100%",
    padding: "10px 14px",
    background: "#0D1526",
    border: "1px solid #1E2D45",
    borderRadius: 10,
    color: "#E2E8F0",
    fontSize: 13,
  };

  const labelStyle: React.CSSProperties = {
    display: "block",
    color: "#64748B",
    fontSize: 11,
    fontWeight: 700,
    marginBottom: 6,
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,.7)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 200,
        padding: 16,
      }}
      onClick={onClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#111827",
          border: "1px solid #1E2D45",
          borderRadius: 20,
          padding: 24,
          width: "100%",
          maxWidth: 460,
          maxHeight: "90vh",
          overflowY: "auto",
        }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 20,
          }}>
          <h3 style={{ color: "#fff", fontWeight: 800, fontSize: 16, margin: 0 }}>
            {notification ? "Bildirishnomani tahrirlash" : "Yangi bildirishnoma"}
          </h3>
          <button
            onClick={onClose}
            style={{
              background: "transparent",
              border: "none",
              color: "#64748B",
              cursor: "pointer",
            }}>
            <X size={18} />
          </button>
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={labelStyle}>Sarlavha *</label>
          <input
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            style={inputStyle}
            placeholder="Yangilanish"
          />
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={labelStyle}>Matn *</label>
          <textarea
            value={form.text}
            onChange={(e) => setForm({ ...form, text: e.target.value })}
            rows={4}
            style={{ ...inputStyle, resize: "vertical" }}
          />
        </div>

        <div style={{ marginBottom: 20 }}>
          <label style={labelStyle}>Rasm (ixtiyoriy)</label>
          <div
            onClick={() => fileInputRef.current?.click()}
            style={{
              width: "100%",
              height: 140,
              borderRadius: 12,
              border: "1px dashed #1E2D45",
              background: "#0D1526",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              overflow: "hidden",
            }}>
            {preview ?
              <img
                src={preview}
                alt="preview"
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
            : <div style={{ textAlign: "center", color: "#475569" }}>
                <ImageOff size={22} style={{ margin: "0 auto 6px" }} />
                <span style={{ fontSize: 12, display: "flex", alignItems: "center", gap: 4 }}>
                  <Upload size={12} /> Rasm yuklash
                </span>
              </div>
            }
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            style={{ display: "none" }}
          />
        </div>

        <div style={{ display: "flex", gap: 10 }}>
          <button
            onClick={onClose}
            disabled={saving}
            style={{
              flex: 1,
              padding: "11px",
              borderRadius: 12,
              border: "1px solid #1E2D45",
              background: "transparent",
              color: "#E2E8F0",
              fontWeight: 700,
              cursor: saving ? "not-allowed" : "pointer",
            }}>
            Bekor
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !form.title.trim() || !form.text.trim()}
            style={{
              flex: 1,
              padding: "11px",
              borderRadius: 12,
              border: "none",
              background: "#3B82F6",
              color: "#fff",
              fontWeight: 700,
              cursor:
                saving || !form.title.trim() || !form.text.trim() ?
                  "not-allowed"
                : "pointer",
              opacity: saving || !form.title.trim() || !form.text.trim() ? 0.7 : 1,
            }}>
            {saving ?
              "Saqlanmoqda..."
            : notification ?
              "Saqlash"
            : "Yaratish"}
          </button>
        </div>
      </div>
    </div>
  );
}
